import { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Stack,
  TextField,
  Typography
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { Icon } from '@iconify/react';
import trash2Outline from '@iconify/icons-eva/trash-2-outline';
import undoOutline from '@iconify/icons-eva/undo-outline';
import { useSnackbar } from 'notistack';
import webServices from '../../services/webServices';
import { throwIfRequestFailed } from '../../services';

// Manage properties — the undo for "Add a property" (card 194e follow-up).
//
// Adding a property was a ONE-WAY DOOR. Nothing in the product could remove
// one, so somebody who added a property by mistake kept it in their switcher
// forever, and the add flow could not be tested end to end by anyone without
// direct database access.
//
// REMOVING ARCHIVES, IT DOES NOT DELETE, and the dialog says so in those words
// before anybody taps it. The address, items, warranties, service history and
// spend all stay exactly where they are; the property simply stops appearing.
// Restoring brings it back untouched. A homeowner should not have to wonder
// whether they are about to destroy four years of records.

ManagePropertiesDialog.propTypes = {
  open: PropTypes.bool,
  onClose: PropTypes.func,
  homes: PropTypes.array,
  activeHomeId: PropTypes.string,
  onChanged: PropTypes.func
};

export default function ManagePropertiesDialog({ open, onClose, homes = [], activeHomeId, onChanged }) {
  const { enqueueSnackbar } = useSnackbar();
  const [labels, setLabels] = useState({});
  const [archived, setArchived] = useState([]);
  const [busy, setBusy] = useState('');
  // Two-step, because removing a property is the kind of thing somebody taps
  // once by accident. The confirm names the property rather than saying "are
  // you sure", which is what makes it possible to notice the wrong one.
  const [confirming, setConfirming] = useState(null);

  const loadArchived = useCallback(async () => {
    const resp = await webServices.getArchivedHomes().catch(() => null);
    if (resp && resp.success === true) setArchived(resp.data || []);
  }, []);

  useEffect(() => {
    if (!open) return;
    setLabels(homes.reduce((all, home) => ({ ...all, [home.id]: home.label }), {}));
    setConfirming(null);
    loadArchived();
  }, [open, homes, loadArchived]);

  const rename = async (home) => {
    const label = labels[home.id] || '';
    if (label === home.label) return;
    setBusy(home.id);
    try {
      throwIfRequestFailed(await webServices.renameHome(home.id, label));
      enqueueSnackbar('Saved.', { variant: 'success' });
      if (onChanged) onChanged();
    } catch (err) {
      enqueueSnackbar(err.message || 'Could not rename that property.', { variant: 'error' });
    } finally {
      setBusy('');
    }
  };

  const archive = async (home) => {
    setBusy(home.id);
    try {
      const result = throwIfRequestFailed(await webServices.archiveHome(home.id));
      // Removing the property you are currently IN moves the whole app, so it
      // gets a reload for the same reason switching does: redux is still
      // holding the items, tasks and budget of a home that is now gone.
      if (result && result.data && result.data.switched) {
        window.location.reload();
        return;
      }
      enqueueSnackbar('Property removed. You can put it back from here.', { variant: 'success' });
      setConfirming(null);
      await loadArchived();
      if (onChanged) onChanged();
    } catch (err) {
      // Includes the last-property refusal, which is written to be read by the
      // homeowner and is surfaced verbatim rather than replaced.
      enqueueSnackbar(err.message || 'Could not remove that property.', { variant: 'error' });
    } finally {
      setBusy('');
    }
  };

  const restore = async (home) => {
    setBusy(home.id);
    try {
      throwIfRequestFailed(await webServices.restoreHome(home.id));
      enqueueSnackbar('Property restored.', { variant: 'success' });
      await loadArchived();
      if (onChanged) onChanged();
    } catch (err) {
      enqueueSnackbar(err.message || 'Could not restore that property.', { variant: 'error' });
    } finally {
      setBusy('');
    }
  };

  return (
    <Dialog open={Boolean(open)} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>Your properties</DialogTitle>
      <DialogContent>
        <Typography variant="body2" sx={{ color: 'text.secondary', mb: 3 }}>
          Name them however you think of them. Removing a property hides it — nothing it holds is deleted, and you can
          put it back from the bottom of this page.
        </Typography>

        <Stack spacing={2}>
          {homes.map((home) => (
            <Box key={home.id}>
              <Stack direction="row" spacing={1} alignItems="center">
                <TextField
                  fullWidth
                  size="small"
                  label={home.id === activeHomeId ? 'This property (currently open)' : 'Property'}
                  placeholder={home.label}
                  value={labels[home.id] === undefined ? home.label : labels[home.id]}
                  onChange={(event) => setLabels({ ...labels, [home.id]: event.target.value })}
                  onBlur={() => rename(home)}
                />
                <IconButton
                  aria-label="Remove this property"
                  disabled={Boolean(busy)}
                  onClick={() => setConfirming(home)}
                >
                  <Icon icon={trash2Outline} width={20} height={20} />
                </IconButton>
              </Stack>

              {confirming && confirming.id === home.id && (
                <Alert
                  severity="warning"
                  sx={{ mt: 1 }}
                  action={
                    <Stack direction="row" spacing={1}>
                      <Button size="small" color="inherit" onClick={() => setConfirming(null)}>
                        Keep it
                      </Button>
                      <LoadingButton
                        size="small"
                        color="warning"
                        loading={busy === home.id}
                        onClick={() => archive(home)}
                      >
                        Remove
                      </LoadingButton>
                    </Stack>
                  }
                >
                  Remove {home.label}? Its items, warranties and history stay saved.
                </Alert>
              )}
            </Box>
          ))}
        </Stack>

        {archived.length > 0 && (
          <>
            <Divider sx={{ my: 3 }} />
            <Typography variant="overline" sx={{ color: 'text.secondary' }}>
              Removed
            </Typography>
            <Stack spacing={1} sx={{ mt: 1 }}>
              {archived.map((home) => (
                <Stack key={home.id} direction="row" spacing={1} alignItems="center">
                  <Typography variant="body2" sx={{ flexGrow: 1, color: 'text.secondary' }}>
                    {home.label}
                  </Typography>
                  <Button
                    size="small"
                    startIcon={<Icon icon={undoOutline} width={18} height={18} />}
                    disabled={Boolean(busy)}
                    onClick={() => restore(home)}
                  >
                    Put it back
                  </Button>
                </Stack>
              ))}
            </Stack>
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Done</Button>
      </DialogActions>
    </Dialog>
  );
}
